import React, { useEffect, useMemo, useState } from 'react';
import useComponentVisible from '../UI/ComponentVisible';
import dotsImgUrl from '../../assets/three-dots-vertical.svg';
import { useUserContext } from '../../contexts/UserContext';
import { Search, Plus, Pencil, Trash2, Coins, Building2 } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { formatDate } from '../../utils/converter';
import { getAllOrganizations, getAllPlans, getOrgPlanMappings } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { AgTableCard } from '../UI';
import OrgPlanMappingModal from './OrgPlanMappingModal';
import DeleteOrgPlanMappingModal from './DeleteOrgPlanMapping';
import ManualTopupModal from './ManualTopupModal';

const ManageOrgPlanMapping = () => {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const { content } = useContentfulContext();
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const { userData } = useUserContext();
  const [gridApi, setGridApi] = useState(null);
  const [plans, setPlans] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const fetchMappings = async () => {
    try {
      const res = await getOrgPlanMappings(userData?.token, userData?.selectedOrganization?.org_token);
      if (res.status) {
        setMappings(res.data);
      }
    } catch (error) {
      console.log("Error fetching mappings:", error);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await getAllPlans(userData.token, userData?.selectedOrganization?.org_token);
      if (res.status) {
        setPlans(res.data);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const res = await getAllOrganizations(userData.token, userData?.selectedOrganization?.org_token);
      if (res.status) {
        setOrganizations(res.data);
        await fetchMappings();
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchOrganizations();
  }, []);

  const colDefs = [
    {
      headerName: content?.ADMIN_ORG_PLAN_MAPPING?.table_headers?.plan_id,
      field: "plan_id",
      sortable: true,
      cellRenderer: ({ data }) => {
        const isOrgLevelZero = data?.plan_id?.org_level === "0";
        return (
          <div className="flex items-center h-full">
            <button
              className="text-left font-medium text-stride-primary hover:text-stride-primary-hover hover:underline transition-colors"
              onClick={() => {
                if (!isOrgLevelZero) {
                  setSelectedMapping(data);
                  setSelectedAction("edit");
                }
              }}
              disabled={isOrgLevelZero}
              style={{
                opacity: isOrgLevelZero ? 0.5 : 1,
                cursor: isOrgLevelZero ? "not-allowed" : "pointer"
              }}
            >
              {data?.plan_id?.plan_name || "—"}
            </button>
          </div>
        );
      },
      filter: true,
      suppressMovable: true,
      minWidth: 180,
    },
    {
      headerName: content?.ADMIN_ORG_PLAN_MAPPING?.table_headers?.org_id,
      field: "org_name",
      sortable: true,
      filter: true,
      suppressMovable: true,
      minWidth: 180,
      cellRenderer: ({ data }) => (
        <div className="flex items-center gap-2 h-full text-stride-heading font-medium">
          <Building2 size={15} className="text-stride-muted shrink-0" />
          <span className="truncate">{data?.org_name || "—"}</span>
        </div>
      )
    },
    {
      headerName: content?.ADMIN_ORG_PLAN_MAPPING?.table_headers?.start_date,
      field: "start_date",
      sortable: true,
      filter: true,
      cellRenderer: ({ data }) => (
        <span className="text-stride-body text-xs">
          {data?.start_date ? formatDate(data?.start_date) : "—"}
        </span>
      ),
      suppressMovable: true,
      minWidth: 150,
    },
    {
      headerName: content?.ADMIN_ORG_PLAN_MAPPING?.table_headers?.end_date,
      field: "end_date",
      sortable: true,
      filter: true,
      cellRenderer: ({ data }) => (
        <span className="text-stride-body text-xs">
          {data?.end_date ? formatDate(data?.end_date) : "N/A"}
        </span>
      ),
      suppressMovable: true,
      minWidth: 150,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ({ data }) => {
        const isOrgLevelZero = data?.plan_id?.org_level === "0";
        if (isOrgLevelZero) {
          return (
            <div className="flex items-center justify-end h-full">
              <span className="text-xs text-stride-muted">System Level</span>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-end gap-1.5 h-full">
            {/* Tactile direct action buttons */}
            {data?.plan_id?.is_custom_plan && (
              <button
                type="button"
                className="stride-table-btn stride-table-btn-secondary"
                title="Add Manual Credits"
                onClick={() => {
                  setSelectedMapping(data);
                  setSelectedAction("manual-topup");
                }}
              >
                <Coins size={13} />
              </button>
            )}
            <button
              type="button"
              className="stride-table-btn stride-table-btn-edit"
              title="Edit Plan Mapping"
              onClick={() => {
                setSelectedMapping(data);
                setSelectedAction("edit");
              }}
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              className="stride-table-btn stride-table-btn-danger"
              title="Delete Plan Mapping"
              onClick={() => {
                setSelectedMapping(data);
                setSelectedAction("delete");
              }}
            >
              <Trash2 size={13} />
            </button>

            {/* Backwards-compatibility 3-dots trigger */}
            <button
              type="button"
              aria-label="Actions"
              className="actions stride-table-btn stride-table-btn-secondary"
              onClick={(e) => {
                setSelectedMapping(data);
                setIsComponentVisible(!isComponentVisible);
                const buttonRect = e.currentTarget.getBoundingClientRect();
                setMenuPosition({
                  top: buttonRect.bottom + window.scrollY,
                  left: buttonRect.left + window.scrollX,
                });
              }}
            >
              <img src={dotsImgUrl} alt="Actions" className="w-3.5 h-3.5 opacity-70" />
            </button>
          </div>
        );
      },
      width: 170,
      minWidth: 170,
      sortable: false,
    }
  ];

  const autoSizeStrategy = useMemo(() => ({
    type: "fitGridWidth",
    defaultMinWidth: 120,
    columnLimits: [
      { colId: "plan_id", minWidth: 180 },
      { colId: "org_name", minWidth: 180 },
      { colId: "start_date", minWidth: 150 },
      { colId: "end_date", minWidth: 150 },
      { colId: "actions", minWidth: 170 }
    ],
  }), []);

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    if (gridApi) {
      gridApi.setFilterModel({
        org_name: { type: 'contains', filter: filterValue },
        plan_id: { type: 'contains', filter: filterValue }
      });
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  return (
    <div className="stride-page-shell">
        <main className="w-full space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1">
                Admin Settings
              </p>
              <h1 className="text-2xl font-semibold tracking-tight text-stride-heading">
                {content?.ADMIN_ORG_PLAN_MAPPING?.title || "Organization Plan Mapping"}
              </h1>
              <p className="text-sm text-stride-body mt-0.5">
                Assign and manage tier plans and credit allocations for active organizations.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <Button
                variant="primary"
                icon={<Plus size={16} />}
                onClick={() => {
                  setSelectedAction("create");
                  setSelectedMapping(null);
                }}
              >
                {content?.ADMIN_ORG_PLAN_MAPPING?.cta_text?.create_mapping || "Create Mapping"}
              </Button>
            </div>
          </div>

          {/* Table Card */}
          <AgTableCard
            header={
              <div className="w-full sm:w-80">
                <Input
                  type="text"
                  placeholder="Search plan or organization..."
                  prefixIcon={<Search size={16} />}
                  onChange={onFilterTextBoxChanged}
                  clearable
                />
              </div>
            }
            tableId="manage-org-plan-mapping-table"
            loading={loading}
            rowData={mappings}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
          >
            {/* Dropdown Menu for backwards compatibility & 3-dots action */}
            {isComponentVisible && selectedMapping && selectedMapping?.plan_id?.org_level !== "0" && (
              <div
                ref={ref}
                className="absolute z-10 w-48 rounded-lg bg-white shadow-lg border border-stride-border py-1 ring-1 ring-black ring-opacity-5 animate-in fade-in zoom-in-95 duration-100"
                style={{ top: menuPosition.top, left: Math.max(16, menuPosition.left - 180) }}
              >
                {selectedMapping?.plan_id?.is_custom_plan && (
                  <a
                    href="#manual-topup"
                    className="block px-4 py-2 text-sm text-stride-heading hover:bg-stride-hover transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsComponentVisible(false);
                      setSelectedAction('manual-topup');
                    }}
                  >
                    Add Manual Credits
                  </a>
                )}
                <a
                  href="#edit"
                  className="block px-4 py-2 text-sm text-stride-heading hover:bg-stride-hover transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsComponentVisible(false);
                    setSelectedAction('edit');
                  }}
                >
                  {content?.ADMIN_ORG_PLAN_MAPPING?.more_info?.edit || "Edit"}
                </a>
                <a
                  href="#delete"
                  className="block px-4 py-2 text-sm text-stride-danger hover:bg-stride-danger/10 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsComponentVisible(false);
                    setSelectedAction('delete');
                  }}
                >
                  {content?.ADMIN_ORG_PLAN_MAPPING?.more_info?.delete || "Delete"}
                </a>
              </div>
            )}
          </AgTableCard>
        </main>

        {selectedAction === "create" && (
          <OrgPlanMappingModal
            organizations={organizations}
            setSelectedAction={setSelectedAction}
            getMappings={fetchMappings}
            plans={plans}
          />
        )}
        {selectedAction === "edit" && (
          <OrgPlanMappingModal
            organizations={organizations}
            setSelectedAction={setSelectedAction}
            selectedMapping={selectedMapping}
            getMappings={fetchMappings}
            plans={plans}
          />
        )}
        {selectedAction === "delete" && (
          <DeleteOrgPlanMappingModal
            setSelectedAction={setSelectedAction}
            selectedMapping={selectedMapping}
            setSelectedMapping={setSelectedMapping}
            getMappings={fetchMappings}
          />
        )}
        {selectedAction === "manual-topup" && (
          <ManualTopupModal
            setSelectedAction={setSelectedAction}
            selectedMapping={selectedMapping}
            getMappings={fetchMappings}
          />
        )}
      </div>
  );
};

export default ManageOrgPlanMapping;
